import ThemeModal from "components/Common/ThemeModal"
import { Badge, Button, Col, Row, Spinner, Table } from "reactstrap"
import { formatReadableDate } from "utils/formatDate"
import { formatCurrency } from "utils/formatCurrency"
import { getEntityLabel, getUserLabel } from "pages/AccountPages/utils/displayValue"

const getMoneyDisplayValue = (value, currency = "BDT") => formatCurrency(value || 0, currency)

const ReceiptRow = ({ label, value, mono = false, strong = false }) => (
  <div className="d-flex align-items-start justify-content-between gap-3 py-2" style={{ borderBottom: "1px dashed rgba(15, 23, 42, 0.12)" }}>
    <span className="text-muted text-uppercase fw-semibold" style={{ fontSize: 10, letterSpacing: 1 }}>{label}</span>
    <span className={`${mono ? "font-monospace" : ""} ${strong ? "fw-bold" : "fw-semibold"} text-dark text-end`.trim()} style={{ fontSize: strong ? 15 : 14 }}>
      {value || "-"}
    </span>
  </div>
)

const ReceiptSection = ({ title, children }) => (
  <div className="pt-3">
    <div className="text-uppercase text-muted fw-semibold mb-2" style={{ fontSize: 11, letterSpacing: 1 }}>{title}</div>
    {children}
  </div>
)

const AccountVendorBillView = ({ modal, toggleModal, bill, loading, canAddPayment = false, onAddPayment }) => {
  const vendorName = getEntityLabel(bill?.vendor, bill?.vendor_id, "Vendor")
  const statusName = getEntityLabel(bill?.bill_status || bill?.billStatus, bill?.bill_status_id, "Status")
  const createdBy = getUserLabel(bill?.createdBy || bill?.created_by)
  const updatedBy = getUserLabel(bill?.updatedBy || bill?.updated_by)
  const totalPaid = bill?.total_paid
  const due = bill?.due
  const payments = bill?.payments || []

  const statusColor =
    statusName?.toLowerCase?.() === "paid" ? "success" :
    statusName?.toLowerCase?.() === "partial" ? "warning" :
    statusName?.toLowerCase?.() === "due" ? "danger" : "secondary"

  return (
    <ThemeModal isOpen={modal} toggle={toggleModal} title="Vendor Bill Details" size="xl" modalBodyClass="bg-body-tertiary">
      {loading ? (
        <div className="text-center py-4"><Spinner size="sm" /></div>
      ) : !bill ? (
        <div className="text-center py-4 text-muted">No details available.</div>
      ) : (
        <div className="mx-auto" style={{ maxWidth: 740 }}>
          <div className="rounded-4 border overflow-hidden" style={{ background: "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)", borderColor: "rgba(15, 23, 42, 0.08)", boxShadow: "0 24px 64px rgba(15, 23, 42, 0.08)" }}>

            <div className="px-4 py-2 d-flex align-items-center justify-content-between" style={{ background: "linear-gradient(90deg, rgba(15, 23, 42, 0.96) 0%, rgba(30, 41, 59, 0.96) 100%)" }}>
              <div className="text-white text-uppercase fw-semibold" style={{ fontSize: 10, letterSpacing: 1.6 }}>Vendor Bill</div>
              <div className="text-white-50 font-monospace" style={{ fontSize: 12 }}>AVB-{String(bill.id ?? "0000").padStart(4, "0")}</div>
            </div>

            <div className="px-4 py-3 border-bottom" style={{ background: "radial-gradient(circle at top left, rgba(139, 92, 246, 0.1), transparent 34%), linear-gradient(180deg, #f8fbfd 0%, #ffffff 100%)", borderColor: "rgba(15, 23, 42, 0.08)" }}>
              <div className="d-flex flex-wrap align-items-start justify-content-between gap-3">
                <div>
                  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 10, letterSpacing: 1.2 }}>Vendor</div>
                  <div className="h5 mb-1 text-dark" style={{ letterSpacing: -0.2 }}>{vendorName}</div>
                  <div className="d-flex flex-wrap gap-2">
                    <Badge color={statusColor} pill className="px-2 py-1">{statusName}</Badge>
                    <span className="text-muted small font-monospace">{bill.bill_no || `#${bill.id}`}</span>
                    <span className="text-muted small">{bill.bill_date ? formatReadableDate(bill.bill_date) : "No date"}</span>
                  </div>
                </div>
                <div className="text-md-end">
                  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 10, letterSpacing: 1.2 }}>Bill Amount</div>
                  <div className="fw-bold text-dark" style={{ fontSize: 34, lineHeight: 0.95, letterSpacing: -1 }}>{getMoneyDisplayValue(bill.amount)}</div>
                  <div className="text-muted small mt-1">from {vendorName}</div>
                </div>
              </div>
            </div>

            <div className="position-relative" style={{ height: 18, background: "#fff" }}>
              <div style={{ position: "absolute", left: 0, right: 0, top: "50%", borderTop: "1px dashed rgba(15, 23, 42, 0.18)" }} />
              <div className="position-absolute rounded-circle bg-body-tertiary" style={{ width: 22, height: 22, left: -11, top: -2 }} />
              <div className="position-absolute rounded-circle bg-body-tertiary" style={{ width: 22, height: 22, right: -11, top: -2 }} />
            </div>

            <div className="px-4 py-3 border-bottom bg-white" style={{ borderColor: "rgba(15, 23, 42, 0.08)" }}>
              <Row className="g-3 g-md-4">
                <Col md={6}>
                  <ReceiptSection title="Bill Details">
                    <ReceiptRow label="Bill Number" value={bill.bill_no || "-"} mono />
                    <ReceiptRow label="Bill Date" value={bill.bill_date ? formatReadableDate(bill.bill_date) : "-"} />
                    <ReceiptRow label="Amount" value={getMoneyDisplayValue(bill.amount)} mono />
                    <ReceiptRow label="Status" value={statusName} />
                  </ReceiptSection>
                </Col>
                <Col md={6}>
                  <ReceiptSection title="Vendor Info">
                    <ReceiptRow label="Vendor" value={vendorName} />
                    <ReceiptRow label="Created By" value={createdBy} />
                    <ReceiptRow label="Updated By" value={updatedBy} />
                    <ReceiptRow label="Record ID" value={bill.id ?? "-"} mono />
                  </ReceiptSection>
                </Col>
              </Row>
            </div>

            <div className="px-4 py-3 border-bottom bg-white" style={{ borderColor: "rgba(15, 23, 42, 0.08)" }}>
              <ReceiptSection title="Payment Summary">
                <div className="rounded-3 px-3 py-2" style={{ background: "rgba(248, 250, 252, 0.9)", border: "1px solid rgba(15, 23, 42, 0.06)" }}>
                  <ReceiptRow label="Vendor" value={vendorName} />
                  <ReceiptRow label="Bill Number" value={bill.bill_no || "-"} mono />
                  <ReceiptRow label="Total Billed" value={getMoneyDisplayValue(bill.amount)} mono />
                  <ReceiptRow label="Total Paid" value={totalPaid !== undefined ? getMoneyDisplayValue(totalPaid) : "-"} mono />
                  <ReceiptRow label="Amount Due" value={due !== undefined ? getMoneyDisplayValue(due) : "-"} strong mono />
                </div>
              </ReceiptSection>
            </div>

            <div className="px-4 py-3 border-bottom bg-white" style={{ borderColor: "rgba(15, 23, 42, 0.08)" }}>
              <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                <ReceiptSection title="Payment History">
                  <div className="text-muted small">Review vendor payments without leaving the bill detail.</div>
                </ReceiptSection>
                {canAddPayment && typeof onAddPayment === "function" && (
                  <Button color="success" size="sm" onClick={() => onAddPayment(bill)}>
                    <i className="mdi mdi-cash-plus me-1" />
                    Add Payment
                  </Button>
                )}
              </div>
              {payments.length ? (
                <div className="table-responsive">
                  <Table size="sm" className="align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Method</th>
                        <th>Account</th>
                        <th className="text-end">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map(item => (
                        <tr key={item.id}>
                          <td>{item.payment_date ? formatReadableDate(item.payment_date) : "-"}</td>
                          <td>{item.payment_method?.name || item.paymentMethod?.name || "-"}</td>
                          <td>{item.account?.name || "-"}</td>
                          <td className="text-end fw-semibold">{getMoneyDisplayValue(item.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div className="text-muted small">No payments recorded yet.</div>
              )}
            </div>

            {bill.notes && (
              <div className="px-4 py-3 border-bottom bg-white" style={{ borderColor: "rgba(15, 23, 42, 0.08)" }}>
                <ReceiptSection title="Notes">
                  <div className="text-dark rounded-3 px-3 py-2" style={{ whiteSpace: "pre-wrap", lineHeight: 1.5, background: "linear-gradient(180deg, rgba(248, 250, 252, 0.95) 0%, rgba(255, 255, 255, 1) 100%)", border: "1px solid rgba(15, 23, 42, 0.06)" }}>
                    {bill.notes}
                  </div>
                </ReceiptSection>
              </div>
            )}

            <div className="px-4 py-3" style={{ background: "linear-gradient(180deg, rgba(248, 250, 252, 0.75) 0%, rgba(241, 245, 249, 0.9) 100%)" }}>
              <Row className="g-3">
                <Col md={4}>
                  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 10, letterSpacing: 1.2 }}>Created By</div>
                  <div className="small text-dark">{createdBy}</div>
                  <div className="small text-muted">{bill.created_at ? formatReadableDate(bill.created_at) : "-"}</div>
                </Col>
                <Col md={4}>
                  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 10, letterSpacing: 1.2 }}>Updated By</div>
                  <div className="small text-dark">{updatedBy}</div>
                  <div className="small text-muted">{bill.updated_at ? formatReadableDate(bill.updated_at) : "-"}</div>
                </Col>
                <Col md={4}>
                  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 10, letterSpacing: 1.2 }}>Record Status</div>
                  <div className="small text-dark fw-semibold">{bill.deleted_at ? "Archived" : "Active"}</div>
                  <div className="small text-muted">{bill.deleted_at ? formatReadableDate(bill.deleted_at) : "No deletion recorded"}</div>
                </Col>
              </Row>
            </div>

          </div>
        </div>
      )}
    </ThemeModal>
  )
}

export default AccountVendorBillView
